/* eslint-disable react/jsx-props-no-spreading */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { decamelize } from 'humps';
import { useTranslation } from 'react-i18next';
import { Gap, Icon, ResultBox, Text, Visibility } from '@reservamos/elements';
import FlightItem from 'components/search/FlightItem';
import MixedItem from 'components/search/MixedItem';
import ResultDefault from 'components/search/ResultDefault';
import SimpleTripResult from 'components/search/SimpleTripResult';
import TripResult from 'components/search/TripResult';
import ResultSpecifics from 'components/search/ResultSpecifics';
import ResultBoxHorizontal from 'components/search/ResultBoxHorizontal';
import dotersDot from 'images/doters/greendoters.svg';
import { useSelector } from 'react-redux';
import SeatSelectorFactory from 'components/seats/SeatSelectorFactory';
import { useHistory } from 'react-router-dom';
import { getProductType } from 'utils/Reserbus';
import { isSeatsOnResultActivated } from 'utils/seats';
import {
  checkRecentTripSeen,
  cleanPrevRecentSeenTrip,
  deleteExpiredRecentSeenTrips,
  saveCacheRecentSeenTrip,
} from 'utils/trips/cacheTrip';
import fingerprintService from 'services/userFingerprint/userFingerprint';
import { quickPurchaseSelectedTracker } from 'metrics/user-analytics/purchase';
import useWhitelabelTheme from '../../hooks/whitelabel/useWhitelabelTheme';
import useSearch from '../../hooks/store/useSearch';
import ResultWithIcons from './ResultWithIcons';
import { flatFareDiscountTotal } from '../../utils/doters';
import 'styles/components/search/ResultBox';
import ResultGfa from './ResultGfa';
import useSearchCouponSelection from './SearchCoupon/SearchCoupon.hooks';
import '../../styles/components/search/TripItem';
import { useBusDiagramEnabled } from '../../utils/personalizationAbTest';
import {
  getPassengerTypesForPricing,
  shouldShowProviderDiscountForSearch,
} from '../../utils/passengerCategories';
import {
  DEFAULT_PASSENGER_CATEGORY,
  OVERBOOKED_PASSENGER_CATEGORY,
} from '../../constants/passengerCategories';
import useGrowthBookFeatureValue from '../GrowthBookProvider/useGrowthBookFeatureValue';
import useViajaAhorrandoTrip from '../../hooks/store/viajaAhorrando/useViajaAhorrandoTrip';
import useExperimentalFeature from '../../hooks/useExperimentalFeature';
import useUserPreferences from '../../hooks/useUserPreferences';
import {
  getBaseEligibility,
  getTripEligibility,
  getInSeatsEligibility,
} from '../../utils/quickPurchaseEligibility';
import useLoyaltyPrograms from '../../loyalty/context/useLoyaltyPrograms';
import buildQuickPurchasePath from '../../utils/quickPurchaseUrl';

/**
 * TripItem component.
 * Renders a trip item in the search results list.
 * @param {object} props - Component props.
 * @returns {JSX.Element} The rendered TripItem component.
 */
const TripItem = (props) => {
  const { t } = useTranslation();
  const { features, env } = useSelector((state) => state.whitelabelConfig);
  const { userIsLoggedInWithAnyLoyaltyProgram, isQuickPurchaseLoyaltySettled = true } =
    useLoyaltyPrograms();
  const {
    resultPriceToShow,
    passengers: searchPassengers,
    departure: searchDeparture,
    couponCode,
  } = useSearch();
  const isBulkTicket = searchDeparture?.routeType === 'bulk_ticket';
  const { resultBoxStyle } = useWhitelabelTheme();
  const isNewResultsDesign = features.NEW_RESULTS_DESIGN;

  // Check if bus diagram should be enabled based on the personalization feature flag
  const busDiagramEnabled = useBusDiagramEnabled();

  // AB Test: Enable pricing based on searched passenger categories
  const categoryBasedPricing = useGrowthBookFeatureValue('category_based_pricing');
  // Fall back to half-price categories (older_half, etc.) before general
  const halfPriceCategoryFallback = useGrowthBookFeatureValue('half_price_category_fallback');

  const quickPurchaseEnabled = useExperimentalFeature('QUICK_PURCHASE_ENABLED');
  const quickCheckoutVariant = useGrowthBookFeatureValue('quick_checkout_variant');

  const { couponLoading } = useSearchCouponSelection();
  const { passengers: frequentPassengers } = useUserPreferences();

  const [showBusDiagram, setShowBusDiagram] = useState(false);

  const history = useHistory();
  const isDesktop = getProductType() === 'desktop';

  const {
    trip,
    type,
    position,
    selectTrip,
    id,
    isFromOpenTicketList,
    filtersApplied,
    isExchange,
    availability,
    isRoundTrip,
    way,
    isLogged,
    departureTrip,
    rewardTicketStatus,
    setRewardTicketStatus,
    highLightAsMobile,
    allowsSeatSelection,
    exchangePassengers,
    isClosed, // Indicates if this trip is closed/unavailable (sold out or departed)
    onQuickPurchaseRequest,
  } = props;

  // Prepare exchange data if available
  const fromExchange =
    isExchange && exchangePassengers
      ? {
          passengers: exchangePassengers,
        }
      : null;

  // Check both if seats on results are enabled by config and by personalization feature flag
  const isSeatsOnResults = isSeatsOnResultActivated() && allowsSeatSelection && busDiagramEnabled;

  /**
   * useEffect hook that cleans up previous recently seen trips when the component unmounts.
   */
  useEffect(() => {
    return () => {
      cleanPrevRecentSeenTrip();
    };
  }, []);

  /**
   * Checks if the trip has an error when fetching the details to close it
   */

  let content;

  const hasFilters =
    !isExchange &&
    (!filtersApplied.departureTime?.includes('none') ||
      filtersApplied.categories?.length ||
      filtersApplied.tripOptions?.length ||
      filtersApplied.reclineOptions?.length ||
      !filtersApplied.stops?.includes('none'));

  const showNewFiltersList = features.NEW_FILTERS_VERSION && hasFilters && !isExchange;
  // eslint-disable-next-line prefer-const
  let { pricing: pricingKey, passengerTypes } = trip;

  if (isRoundTrip)
    pricingKey = way === 'departure' ? trip.departureRoundTripPricing : trip.roundTripPricing;

  const { walletPoints, totalBase, discountBase, providerDiscount, providerDiscounts } = pricingKey;

  // Get Viaja Ahorrando discount from search reducer (similar to wallet)
  const viajaAhorrandoDiscount = useViajaAhorrandoTrip(id);
  const viajaAhorrandoTotal = viajaAhorrandoDiscount?.total;
  const isViajaAhorrando = Boolean(viajaAhorrandoDiscount);

  const { flatFareValid } = flatFareDiscountTotal({
    way,
    trip,
    isRoundTrip,
    departureTrip,
    isFlatFareEnabled: features.FLAT_FARE_ENABLED,
  });
  const showFlatFare = flatFareValid === 'valid';
  const isRecentlySeen = checkRecentTripSeen(trip.id);

  const isNewUserForDigitalDiscount = fingerprintService.isFirstSession();

  // Extract flat_fare from providerDiscounts
  const flatFareDiscount =
    providerDiscounts?.find((discount) => discount.name?.includes('flat_fare')) ?? null;

  // Check if there are other discounts besides flat_fare
  const hasOtherDiscounts = providerDiscounts?.some(
    (discount) => !discount.name?.includes('flat_fare'),
  );

  // Determine if flat fare should be included in price calculation
  // using flat fare if is the only discount available
  const shouldIncludeFlatFare =
    showFlatFare &&
    flatFareDiscount &&
    (features.USE_FLAT_FARE_AS_RESULT_PRICE || !hasOtherDiscounts);

  // Get passenger types to consider for minimum price calculation
  const typesToFilter = getPassengerTypesForPricing(
    searchPassengers,
    passengerTypes,
    Boolean(categoryBasedPricing),
    Boolean(halfPriceCategoryFallback),
  );

  // Find lowest available price from all valid discounts and searched passenger types
  const lowestAvailablePrice = [
    // Flat fare discount (only if conditions are met)
    ...(shouldIncludeFlatFare ? [flatFareDiscount] : []),
    // Provider discounts (excluding flat fare)
    ...(providerDiscounts?.filter((d) => !d.name?.includes('flat_fare')) || []),
    // Passenger types that were searched (or fallback to general/oversold)
    ...(passengerTypes?.filter((t) => typesToFilter.includes(t.type)) || []),
  ]
    .filter((d) => d.availability > 0)
    .reduce(
      (lowest, current) =>
        Number(current.total) < Number(lowest?.total || Infinity) ? current : lowest,
      null,
    );

  // The displayed price comes from a searched passenger category, not a provider discount
  const isCategoryPrice =
    Boolean(lowestAvailablePrice?.type) &&
    lowestAvailablePrice.type !== DEFAULT_PASSENGER_CATEGORY &&
    lowestAvailablePrice.type !== OVERBOOKED_PASSENGER_CATEGORY;

  /**
   * Function to handle selecting a trip item
   */
  const onSelectTrip = () => {
    selectTrip(trip, position);
    deleteExpiredRecentSeenTrips();
  };

  /**
   * Handles the selection of a trip item, showing the bus diagram if applicable.
   */
  const handleOnSelect = () => {
    const isRedirect = features.TRIP_REDIRECTION_LEAD && trip.redirectTo;

    const canShowBusDiagram =
      isSeatsOnResults && !isRedirect && !trip.hasConnections && !departureTrip?.hasConnections;

    if (canShowBusDiagram) {
      if (showBusDiagram) {
        cleanPrevRecentSeenTrip();
      }
      setShowBusDiagram((prevState) => !prevState);
      saveCacheRecentSeenTrip(trip.id);
      return;
    }

    if (features.FLAT_FARE_ENABLED && way === 'return') {
      if (
        flatFareValid === 'noExists' ||
        flatFareValid === 'valid' ||
        rewardTicketStatus.status === 'continue'
      ) {
        onSelectTrip();
      }
      if (flatFareValid === 'invalid') {
        setRewardTicketStatus({
          status: 'invalid',
          onAccept: () => onSelectTrip(),
        });
      }
    } else {
      onSelectTrip();
    }
  };

  /* Prices */
  // Use Viaja Ahorrando price if available, otherwise closed/lowest available price
  let priceTotal = Number(lowestAvailablePrice?.total) || 0;
  if (isViajaAhorrando) priceTotal = Number(viajaAhorrandoTotal) || 0;
  else if (isClosed) priceTotal = Number(pricingKey?.total) || 0;
  const discountAmount = providerDiscount ? providerDiscount.amount : 0;
  const discount = discountBase || discountAmount;
  const windowTicketPrice = priceTotal + discount;

  // Only show provider discount advice when displaying general category price
  // If specific categories are filtered (student, older, etc.), the discount doesn't apply to them
  const shouldShowProviderDiscount = shouldShowProviderDiscountForSearch(
    searchPassengers,
    passengerTypes,
    Boolean(categoryBasedPricing),
    Boolean(halfPriceCategoryFallback),
  );

  const isRoundTripContext = isRoundTrip || (way === 'return' && Boolean(departureTrip));
  // Quick Purchase: visibility and handler
  const {
    eligible: baseEligible,
    eligibleWithoutPassengers,
    mergedPassengerCount,
  } = getBaseEligibility({
    quickPurchaseEnabled,
    searchPassengers,
    frequentPassengers,
    features,
    isRoundTrip: isRoundTripContext,
    isExchange,
    isBulkTicket,
    isQuickPurchaseLoyaltySettled,
    userIsLoggedInWithAnyLoyaltyProgram,
  });
  const showQuickPurchase = getTripEligibility(trip, baseEligible, features);

  /**
   * Handles the opening of quick purchase. In modal or screen variation.
   * @param {object} params - Quick purchase params.
   * @param {string} params.passengersParam - Passenger selection param.
   * @param {Array<string>} params.seatNumbers - Selected seat numbers.
   * @param {string} params.source - Source of the quick purchase.
   */
  const openOrNavigateToQuickPurchase = ({ passengersParam, seatNumbers, source }) => {
    const departureId = trip.id;
    const returnId = departureTrip?.id || '';
    const seenPrice = trip.pricing?.total || '';

    if (onQuickPurchaseRequest && quickCheckoutVariant === 'modal') {
      onQuickPurchaseRequest({
        departureId,
        returnId,
        passengersParam,
        seenPrice,
        seatNumbers,
        source,
      });
      return;
    }

    // Quick purchase is created after this route transition, so the search coupon
    // must travel in the URL to become external_coupon_code in the purchase payload.
    history.push(
      buildQuickPurchasePath({
        departureId,
        returnId,
        passengersParam,
        seenPrice,
        seatNumbers,
        couponCode,
      }),
    );
  };

  // Quick Purchase from seat selection: uses selected seat count instead of search passengers
  const canShowQuickPurchaseInSeats = getInSeatsEligibility(
    trip,
    eligibleWithoutPassengers,
    features,
  );
  const frequentPassengersCount = mergedPassengerCount;

  /**
   * Sends the user to quick purchase from the search result.
   */
  const handleQuickPurchase = () => {
    quickPurchaseSelectedTracker('schedule', Boolean(trip.recommendationConcept));
    const passengersParam = searchPassengers.passengers || 'A1';

    openOrNavigateToQuickPurchase({
      passengersParam,
      source: 'schedule',
    });
  };

  /**
   * Sends the user to quick purchase from the seat selection.
   */
  const handleQuickPurchaseFromSeats = (seatCount, seatNumbers) => {
    quickPurchaseSelectedTracker('seats', Boolean(trip.recommendationConcept));
    const passengersParam = `A${seatCount}`;

    openOrNavigateToQuickPurchase({
      passengersParam,
      seatNumbers,
      source: 'seats',
    });
  };

  const itemProps = {
    ...props,
    tripId: id,
    hasFilters,
    useFlatFare: flatFareValid,
    showNewFiltersList,
    walletPoints,
    ...((isLogged || isExchange) && {
      totalBase,
    }),
    discountBase,
    isExchange,
    onSelectClick: handleOnSelect,
    showWalletPoints: resultPriceToShow.priceType === 'wallet',
    walletType: resultPriceToShow.walletType,
    total: isViajaAhorrando ? viajaAhorrandoTotal : priceTotal,
    isViajaAhorrando,
    windowTicketPrice,
    highLightType: trip.recommendationConcept,
    highlightText: `${t(`trips:recommended_trips.${decamelize(trip.recommendationType || '')}`)}`,
    sparkleBadgeLabel: trip.recommendationConcept
      ? t('trips:recommended_trips.badge_label')
      : undefined,
    highLightAsMobile,
    loadingPrice: couponLoading,
    recentView: isRecentlySeen,
    isSeatsOnResults,
    shouldShowProviderDiscount,
    isCategoryPrice,
    isNewUser: isNewUserForDigitalDiscount,
    pricing: pricingKey,
  };

  if (!isFromOpenTicketList && itemProps.trip && itemProps.trip.openTicket) {
    return null;
  }

  // TODO: Remove this when the AB test is finished and the config is set with the correct values
  const resultsForAb = {
    'berlinas': 'simpleResult',
    'rochoa': 'simpleResult',
    'roll-bits': 'simpleResult',
    'gfa': 'simpleResult',
    'etn': 'result',
    'gho': 'result',
    'costaline': 'result',
    'brasilia': 'result',
    'default': features.SCHEDULE_RESULT_TYPE,
  };

  let resultType = features.SCHEDULE_RESULT_TYPE;
  if (isNewResultsDesign) {
    resultType = resultsForAb[env.brand] || resultsForAb.default;
  }

  const shouldShowBusDiagram = Boolean(showBusDiagram && busDiagramEnabled);
  const isBusDiagramOpen = shouldShowBusDiagram && isDesktop;
  const isBusDiagramOpenMobile = shouldShowBusDiagram && !isDesktop;

  const scheduleBox = {
    default: <ResultDefault {...itemProps} />,
    GFA: <ResultGfa {...itemProps} />,
    specifics: <ResultSpecifics {...itemProps} />,
    horizontal: (
      <>
        <Visibility type="hideForMobileOnly">
          <ResultBoxHorizontal {...itemProps} />
        </Visibility>
        <Visibility type="showForMobileOnly">
          <ResultSpecifics {...itemProps} />
        </Visibility>
      </>
    ),
    icons: <ResultWithIcons {...itemProps} />,
    result: (
      <TripResult
        {...itemProps}
        isOpen={isBusDiagramOpen}
        showQuickPurchase={showQuickPurchase && !isBusDiagramOpen}
        onQuickPurchaseClick={handleQuickPurchase}
      >
        <SeatSelectorFactory
          way={way}
          trip={trip}
          fromTrip={departureTrip}
          isPurchaseView={false}
          finishSeatsSelection={onSelectTrip}
          resultsLayout
          isRoundTrip={isRoundTripContext}
          fromExchange={fromExchange}
          onQuickPurchaseFromSeats={
            canShowQuickPurchaseInSeats ? handleQuickPurchaseFromSeats : null
          }
          frequentPassengersCount={frequentPassengersCount}
        />
      </TripResult>
    ),
    simpleResult: (
      <SimpleTripResult
        {...itemProps}
        isOpen={isBusDiagramOpen}
        showQuickPurchase={showQuickPurchase && !isBusDiagramOpen}
        onQuickPurchaseClick={handleQuickPurchase}
      >
        <SeatSelectorFactory
          way={way}
          trip={trip}
          fromTrip={departureTrip}
          isPurchaseView={false}
          finishSeatsSelection={onSelectTrip}
          resultsLayout
          isRoundTrip={isRoundTripContext}
          fromExchange={fromExchange}
          onQuickPurchaseFromSeats={
            canShowQuickPurchaseInSeats ? handleQuickPurchaseFromSeats : null
          }
          frequentPassengersCount={frequentPassengersCount}
        />
      </SimpleTripResult>
    ),
  };

  switch (itemProps.type || type) {
    case 'bus':
      content = scheduleBox[resultType] || <ResultDefault {...itemProps} />;
      break;
    case 'flight':
      content = <FlightItem {...itemProps} />;
      break;
    case 'mixed':
      content = <MixedItem {...itemProps} />;
      break;
    default:
      content = <div />;
  }

  const { enabled: lastSeatsEnabled, quantity: lastSeatsMin } = features.LAST_SEATS_MESSAGE || {};
  const lastSeatMessages =
    lastSeatsEnabled && availability <= lastSeatsMin ? (
      <Text color="white" size="S" weight="semibold">
        {t('trips:label.last_seats_available')} 📢
      </Text>
    ) : null;

  let notice = lastSeatMessages;
  let noticePosition = false;
  let noticeBg = false;

  const dotersMessage = (
    <Gap alignItems="center">
      <Text color="white" size="S">
        {t('purchase:label.reward_ticket', { ...(isRoundTrip && { context: way }) })}
      </Text>
      <Icon type={dotersDot} size="S" />
    </Gap>
  );

  const noValidFlatFare = (
    <Gap alignItems="center">
      <Text color="grayStrong" size="S">
        {t('purchase:label.reward_ticket_invalid', { context: features.FLAT_FARE_CONTEXT })}
      </Text>
    </Gap>
  );

  if (flatFareValid && !isExchange) {
    if (flatFareValid === 'valid') notice = dotersMessage;
    if (flatFareValid === 'invalid') notice = noValidFlatFare;
    if (flatFareValid === 'valid' || flatFareValid === 'invalid') noticePosition = 'right';
    if (flatFareValid === 'valid') noticeBg = 'grayStrong';
    if (flatFareValid === 'invalid') noticeBg = 'warning';
  }

  const withoutResultBox =
    isNewResultsDesign === true || resultType === 'result' || resultType === 'simpleResult';

  // Apply disabled styling for closed trips
  const closedTripClasses = isClosed ? 'opacity-60 pointer-events-none' : '';
  const containerClasses = trip.recommendationConcept
    ? `recommended-trip-container ${closedTripClasses}`
    : closedTripClasses;

  return withoutResultBox ? (
    <>
      <div className={containerClasses}>{content}</div>
      {isBusDiagramOpenMobile && !isClosed && (
        <SeatSelectorFactory
          onCloseModal={() => setShowBusDiagram(false)}
          trip={trip}
          way={way}
          history={history}
          finishSeatsSelection={onSelectTrip}
          fromTrip={departureTrip}
          isPurchaseView={false}
          resultsLayout
          isRoundTrip={isRoundTrip}
          onQuickPurchaseFromSeats={
            canShowQuickPurchaseInSeats ? handleQuickPurchaseFromSeats : null
          }
          frequentPassengersCount={frequentPassengersCount}
        />
      )}
    </>
  ) : (
    <div className={closedTripClasses}>
      <ResultBox
        onClick={isNewResultsDesign === false || isClosed ? null : handleOnSelect}
        notice={notice}
        noticePosition={noticePosition}
        noticeBg={noticeBg}
        flat={resultBoxStyle === 'flat'}
        outline={resultBoxStyle === 'outline'}
      >
        {content}
      </ResultBox>
    </div>
  );
};

TripItem.propTypes = {
  trip: PropTypes.object.isRequired,
  id: PropTypes.string.isRequired,
  position: PropTypes.number.isRequired,
  selectTrip: PropTypes.func.isRequired,
  isFromOpenTicketList: PropTypes.bool,
  type: PropTypes.string,
  availability: PropTypes.number.isRequired,
  filtersApplied: PropTypes.object,
  isExchange: PropTypes.bool,
  exchangePassengers: PropTypes.array,
  isRoundTrip: PropTypes.bool,
  way: PropTypes.string.isRequired,
  isLogged: PropTypes.bool,
  departureTrip: PropTypes.object,
  rewardTicketStatus: PropTypes.object,
  setRewardTicketStatus: PropTypes.func,
  total: PropTypes.number.isRequired,
  highLightAsMobile: PropTypes.bool,
  allowsSeatSelection: PropTypes.bool,
  isClosed: PropTypes.bool, // If true, applies disabled styling for closed trips
  onQuickPurchaseRequest: PropTypes.func,
};

TripItem.defaultProps = {
  type: 'bus',
  onQuickPurchaseRequest: null,
};

export default TripItem;
